import {
	ActionButton,
	Button,
	ButtonGroup,
	Content,
	DropZone,
	Heading,
	IllustratedMessage,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	ProgressCircle,
	Text,
	TextArea,
	TextField,
} from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import ImageAdd from '@react-spectrum/s2/icons/ImageAdd';
import More from '@react-spectrum/s2/icons/More';
import CloudUpload from '@react-spectrum/s2/illustrations/gradient/generic1/CloudUpload';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useEffect, useRef, useState } from 'react';

import {
	IMAGE_FILE_INPUT_ACCEPT,
	isAcceptedImageDropType,
} from '@/api/fileUploadApi';
import { useUserId } from '@/auth/UserIdContext';
import { AnimateIn } from '@/components/AnimateIn';
import { BlobImage } from '@/components/blob/BlobImage';
import { useAddImageToLibrary } from '@/hooks/useAddImageToLibrary';
import { useImageUpload, useImageUploadReset } from '@/hooks/useImageUpload';

import type {
	BusinessProfile,
	Product,
} from '../../../../../bridge/types/business-profile.ts';
import { cardTitleStyles, CARD } from '../styles';

const productNameStyles = style({
	font: 'body',
	fontWeight: 'bold',
	fontSize: 'body-sm',
});

type ProductCardProps = {
	product: Product;
	index: number;
	products: Product[];
	initialEditing: boolean;
	onSave: (path: string, value: string) => void;
	onArraySave: (path: string, value: string[]) => void;
	onRemoveProduct: (index: number) => void;
	onOptimisticUpdate: (products: Product[]) => void;
	onClearNewProduct: () => void;
};

const ProductCard = memo(function ProductCard({
	product,
	index,
	products,
	initialEditing,
	onSave,
	onArraySave,
	onRemoveProduct,
	onOptimisticUpdate,
	onClearNewProduct,
}: ProductCardProps) {
	const userId = useUserId();
	const [isEditing, setIsEditing] = useState(initialEditing);
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
	const [draftName, setDraftName] = useState('');
	const [draftDescription, setDraftDescription] = useState('');
	const [draftPrice, setDraftPrice] = useState('');

	const cardRef = useRef<HTMLDivElement>(null);
	const imageUpload = useImageUpload(userId);
	const addImageToLibrary = useAddImageToLibrary(userId);
	const [imageError, setImageError] = useState(false);
	const imageFileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isEditing) {
			cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const basePath = `products.product.${index}`;
	const image = product.images?.value?.[0];

	useImageUploadReset(imageUpload, image);

	// Both the file picker and the drop zone funnel through
	// useImageUpload.handleFile and discard its return, so there is no single
	// call site to hook. React to the shared url state instead: it starts null
	// and is only set by a successful upload (useImageUploadReset nulls it again
	// when the product's server image changes), so this fires once per genuine
	// upload. Server-side dedup-by-url makes any accidental re-fire harmless.
	useEffect(() => {
		if (imageUpload.url) addImageToLibrary(imageUpload.url, 'product');
	}, [imageUpload.url, addImageToLibrary]);

	const handleEdit = () => {
		setDraftName(product.name?.value ?? '');
		setDraftDescription(product.description?.value ?? '');
		setDraftPrice(product.price?.value ?? '');
		setIsEditing(true);
	};

	const handleSave = () => {
		if (draftName !== (product.name?.value ?? '')) {
			onSave(`${basePath}.name`, draftName);
		}
		if (draftDescription !== (product.description?.value ?? '')) {
			onSave(`${basePath}.description`, draftDescription);
		}
		if (draftPrice !== (product.price?.value ?? '')) {
			onSave(`${basePath}.price`, draftPrice);
		}
		if (imageUpload.url) {
			onArraySave(`${basePath}.images`, [imageUpload.url]);
		}

		const updated = [...products];
		updated[index] = {
			...updated[index],
			name: { value: draftName || null, inferred: false },
			description: { value: draftDescription || null, inferred: false },
			price: { value: draftPrice || null, inferred: false },
			images: imageUpload.url
				? { value: [imageUpload.url], inferred: false }
				: updated[index].images,
		};
		onOptimisticUpdate(updated);

		setIsEditing(false);
		onClearNewProduct();
	};

	const handleCancel = () => {
		setIsEditing(false);
		onClearNewProduct();
		imageUpload.reset();
	};

	const ImageDropZone = ({
		isFilled,
		showImage,
	}: {
		isFilled: boolean;
		showImage?: string | null;
	}) => (
		<DropZone
			onDrop={imageUpload.handleDrop}
			getDropOperation={(types) =>
				isAcceptedImageDropType(types) ? 'copy' : 'cancel'
			}
			isFilled={isFilled}
			replaceMessage="Drop file to replace"
			styles={style({ height: 'full', maxHeight: 'full' })}
		>
			<div
				className="flex h-full w-full cursor-pointer flex-col items-center
					justify-center"
				onClick={() => imageFileInputRef.current?.click()}
			>
				{imageUpload.isUploading ? (
					<ProgressCircle aria-label="Uploading image" isIndeterminate />
				) : showImage ? (
					<BlobImage
						src={showImage}
						alt="Product preview"
						className="h-full w-full object-cover"
					/>
				) : (
					<IllustratedMessage>
						<CloudUpload />
						<Heading>Drag and drop</Heading>
						<Content>Or click to browse</Content>
					</IllustratedMessage>
				)}
			</div>
			<input
				ref={imageFileInputRef}
				type="file"
				accept={IMAGE_FILE_INPUT_ACCEPT}
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) imageUpload.handleFile(file);
					e.target.value = '';
				}}
			/>
		</DropZone>
	);

	return (
		<div
			ref={cardRef}
			className={`${CARD} group flex flex-col overflow-hidden !p-0`}
		>
			{/* Image Preview */}
			<div
				className={`relative aspect-[4/3] w-full overflow-hidden bg-gray-100
					${isEditing ? 'px-4 pt-4' : ''}`}
			>
				{isEditing ? (
					<div className="h-full w-full overflow-hidden rounded-lg">
						<ImageDropZone
							isFilled={!!imageUpload.preview || !!(image && !imageError)}
							showImage={
								imageUpload.preview || (image && !imageError ? image : null)
							}
						/>
					</div>
				) : image && !imageError ? (
					<BlobImage
						src={image}
						alt={product.name?.value ?? 'Product'}
						className="h-full w-full object-cover"
						onError={() => setImageError(true)}
					/>
				) : (
					<div className="flex h-full w-full items-center justify-center">
						<ImageAdd styles={iconStyle({ size: 'XL' })} />
					</div>
				)}
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col gap-3 p-4">
				{isEditing ? (
					<>
						<TextField label="Name" value={draftName} onChange={setDraftName} />
						<TextArea
							label="Description"
							value={draftDescription}
							onChange={setDraftDescription}
						/>
						<TextField
							label="Price"
							value={draftPrice}
							onChange={setDraftPrice}
						/>
						<div className="mt-2 flex justify-end gap-2">
							<Button variant="secondary" size="S" onPress={handleCancel}>
								Cancel
							</Button>
							<Button variant="accent" size="S" onPress={handleSave}>
								Save
							</Button>
						</div>
					</>
				) : isConfirmingDelete ? (
					<div
						className="flex flex-1 flex-col items-center justify-center gap-4"
					>
						<Text>Are you sure you want to delete?</Text>
						<ButtonGroup>
							<Button
								variant="secondary"
								onPress={() => setIsConfirmingDelete(false)}
							>
								Cancel
							</Button>
							<Button variant="negative" onPress={() => onRemoveProduct(index)}>
								Delete
							</Button>
						</ButtonGroup>
					</div>
				) : (
					<>
						<div className="flex items-start justify-between gap-2">
							<Heading level={4} styles={productNameStyles}>
								{product.name?.value || 'Untitled Product'}
							</Heading>
							<MenuTrigger>
								<ActionButton
									aria-label={`More options for ${product.name?.value || 'product'}`}
									isQuiet
									size="XS"
								>
									<More />
								</ActionButton>
								<Popover>
									<Menu
										onAction={(key) => {
											if (key === 'edit') {
												handleEdit();
											} else if (key === 'delete') {
												setIsConfirmingDelete(true);
											}
										}}
									>
										<MenuItem id="edit">Edit</MenuItem>
										<MenuItem id="delete">Delete</MenuItem>
									</Menu>
								</Popover>
							</MenuTrigger>
						</div>

						<p className="text-sm text-[#505050]">
							{product.description?.value || 'No description'}
						</p>

						<span className="grow" />

						{product.price?.value &&
							(() => {
								const stripped = product.price.value.replace(/\$/g, '');
								return (
									<Text styles={productNameStyles}>
										{isNaN(Number(stripped))
											? product.price.value
											: `$${stripped}`}
									</Text>
								);
							})()}
					</>
				)}
			</div>
		</div>
	);
});

type ProductsTabProps = {
	profile: BusinessProfile | undefined;
	optimisticProducts: Product[] | null;
	newProductIndex: number | null;
	onSave: (path: string, value: string) => void;
	onArraySave: (path: string, value: string[]) => void;
	onAddProduct: () => void;
	onRemoveProduct: (index: number) => void;
	onOptimisticUpdate: (products: Product[]) => void;
	onClearNewProduct: () => void;
};

export const ProductsTab = memo(function ProductsTab({
	profile,
	optimisticProducts,
	newProductIndex,
	onSave,
	onArraySave,
	onAddProduct,
	onRemoveProduct,
	onOptimisticUpdate,
	onClearNewProduct,
}: ProductsTabProps) {
	const serverProducts = profile?.products?.product ?? [];
	const products = optimisticProducts ?? serverProducts;

	if (products.length === 0) {
		return (
			<AnimateIn>
				<div className="flex flex-col items-center justify-center gap-4 py-16">
					<p className="text-sm text-[var(--text-secondary)]">
						No products yet
					</p>
					<Button variant="accent" size="S" onPress={onAddProduct}>
						<Add />
						<Text>Add product</Text>
					</Button>
				</div>
			</AnimateIn>
		);
	}

	return (
		<AnimateIn>
			<div className="flex flex-col gap-6">
				<div className="flex items-center justify-between">
					<Heading level={3} styles={cardTitleStyles}>
						Products
					</Heading>
					<Button variant="primary" size="S" onPress={onAddProduct}>
						<Add />
						<Text>Add product</Text>
					</Button>
				</div>

				<div className="grid grid-cols-3 gap-4">
					{products.map((product, index) => (
						<ProductCard
							key={index}
							product={product}
							index={index}
							products={products}
							initialEditing={index === newProductIndex}
							onSave={onSave}
							onArraySave={onArraySave}
							onRemoveProduct={onRemoveProduct}
							onOptimisticUpdate={onOptimisticUpdate}
							onClearNewProduct={onClearNewProduct}
						/>
					))}
				</div>
			</div>
		</AnimateIn>
	);
});
